import axios from 'axios';
import axiosRetry from 'axios-retry';
import { GetStaticPaths, GetStaticProps } from 'next';
import React from 'react';
import { teacherDTOtoTeacher } from '../../adapters/mappers/teacherMapper';
import Layout from '../../components/layout/layout';
import TeacherProfile from '../../components/teacher/profile/teacherProfile';
import { MarketingTeacher } from '../../domain/teacher';
import generateTeacherCollectionQuery from '../../graphql/queries/generateTeacherCollectionQuery';
import { getMarketingTeacher } from '../../graphql/queries/getMarketingTeachers';
import { TeacherFragment } from '../../typescript/generated/codegen';

interface TeacherProfileProps {
  teacher: MarketingTeacher;
}

const Teacher: React.FC<TeacherProfileProps> = ({ teacher }) => {
  if (!teacher) return null;

  return (
    <Layout title={teacher.name} isTransparent>
      <TeacherProfile teacher={teacher} />
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const marketingRequestClient = axios.create({
    baseURL: `https://graphql.contentful.com/`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${process.env.NEXT_PUBLIC_MARKETING_CONTENTFUL_ACCESS_TOKEN}`,
    },
  });
  axiosRetry(marketingRequestClient, {
    retryDelay: axiosRetry.exponentialDelay,
  });

  const marketingRequest = await marketingRequestClient.post(
    `content/v1/spaces/${process.env.NEXT_PUBLIC_MARKETING_CONTENTFUL_SPACE_ID}/environments/${process.env.NEXT_PUBLIC_MARKETING_CONTENTFUL_ENVIRONMENT}`,
    { query: getMarketingTeacher(params?.teacherName as string) }
  );

  // teacher object from marketing request
  const marketingTeacher =
    marketingRequest.data?.data?.teacherCollection?.items[0];

  // if marketingTeacher set then return as prop
  if (marketingTeacher) {
    return {
      props: { teacher: teacherDTOtoTeacher(marketingTeacher) },
      revalidate: 60 * 60 * 8,
    };
  } else {
    // if teacher is not found in marketing CTF then query in content CTF and set as fallback teacher data
    const contentfulRequestClient = await axios.create({
      baseURL: `https://graphql.contentful.com/`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN}`,
      },
    });
    axiosRetry(contentfulRequestClient, {
      retryDelay: axiosRetry.exponentialDelay,
    });

    const contentfulRequest = await contentfulRequestClient.post(
      `content/v1/spaces/${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID}/environments/${process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT}`,
      {
        query: generateTeacherCollectionQuery({
          where: { slug: params?.teacherName },
        }),
      }
    );

    const fallbackTeacher: TeacherFragment =
      contentfulRequest?.data?.data?.teacherCollection?.items[0];

    if (fallbackTeacher) {
      const mappedTeacher = {
        beid: fallbackTeacher.beid,
        slug: fallbackTeacher.slug,
        name: `${fallbackTeacher.firstName} ${fallbackTeacher.lastName}`,
        bio: fallbackTeacher.description,
        bannerImage:
          fallbackTeacher.heroImage ||
          fallbackTeacher.image43 ||
          fallbackTeacher.image,
        profileImage: fallbackTeacher.image || fallbackTeacher.image43,
      };

      return {
        props: { teacher: mappedTeacher },
        revalidate: 60 * 60 * 8,
      };
    } else {
      return {
        notFound: true,
      };
    }
  }
};

export default Teacher;
